import { array, bool, boolean, object, string } from "yup";
import { formValidationErrorMessages, isoDateString, nonEmptyRequiredString, nonEmptyString, optionalCheckbox, postalCodeValidationRule, } from "../shared.js";
import { soadAssessmentCompletionStatusSchema } from "./helpers.js";
import { assessmentDelayCauseOptions, minAssessmentDelayDays, } from "./srfAssessmentDelays.js";
const wholeNumber = /^\d+$/;
const addressSchema = object({
    address: nonEmptyString
        .required("Please enter where the assessment took place")
        .max(255, formValidationErrorMessages.tooManyCharactersError),
    postalCode: postalCodeValidationRule("type", "hospital"),
    isConfirmed: bool().required().isTrue("Please confirm your address"),
}).default({
    address: "",
    isConfirmed: false,
    postalCode: "",
});
// assessment
export const soadReportFormPart1DataSchema = object({
    dateOfAssessment: isoDateString.required("Please enter the date of the initial assessment"),
    assessmentConducted: boolean().default(true),
    assessmentType: nonEmptyString
        .default("")
        .required("Please enter how the assessment was conducted"),
    assessmentAddress: addressSchema
        .default(null)
        .nullable()
        .when("assessmentType", {
        is: (assessmentType) => assessmentType === "inPerson",
        then: () => addressSchema.required("Please enter where the assessment took place"),
        otherwise: () => addressSchema.default(null).nullable(),
    }),
    assessmentTypeText: nonEmptyRequiredString,
    otherAssessments: array()
        .of(object({
        assessmentConducted: boolean().default(true),
        assessmentType: nonEmptyString
            .default("")
            .required("Please enter how the assessment was conducted"),
        assessmentAddress: addressSchema
            .default(null)
            .nullable()
            .when("assessmentType", {
            is: (assessmentType) => assessmentType === "inPerson",
            then: () => addressSchema.required("Please enter where the assessment took place"),
            otherwise: () => addressSchema.default(null).nullable(),
        }),
        assessmentTypeText: nonEmptyRequiredString,
        dateOfAssessment: isoDateString.required("Please enter the date of the assessment"),
        reasonForAssessment: string()
            .default(null)
            .required("Please enter the reason for the additional assessment"),
    }))
        .default([]),
    mentalHealthActSection: nonEmptyString
        .default("")
        .required("Please enter the section of the Mental Health Act that the patient is under"),
    mentalHealthActSectionText: nonEmptyRequiredString,
    ethnicity: nonEmptyString
        .default("")
        .required("Please select the patient's ethnic group"),
    ethnicityText: nonEmptyString.default(""),
    ethnicityDetermined: nonEmptyString
        .default("")
        .required("Please select how you determined the patient's ethnic group"),
    ethnicityDeterminedOther: string()
        .default(null)
        .nullable()
        .when("ethnicityDetermined", {
        is: (ethnicityDetermined) => ethnicityDetermined === "other",
        then: () => nonEmptyRequiredString.required("Please specify"),
        otherwise: () => string().default(null).nullable(),
    }),
    delayedOutsideSoadControl: boolean()
        .default(null)
        .required("Please select whether you were delayed due to issues outside of your control"),
    delayCauseProviderClinicalInformation: optionalCheckbox,
    delayCauseFurtherInformationResponse: optionalCheckbox,
    delayCausePatientRecordsAccess: optionalCheckbox,
    delayCauseProviderArrangingAssessment: optionalCheckbox,
    delayCauseProviderRescheduled: optionalCheckbox,
    delayCauseInterpreterArrangements: optionalCheckbox,
    delayCauseTravelOrLogistics: optionalCheckbox,
    delayCauseSecurityOrEscorting: optionalCheckbox,
    delayCausePatientTransferred: optionalCheckbox,
    delayCausePatientAbsent: optionalCheckbox,
    delayCausePatientTooUnwell: optionalCheckbox,
    delayCauseInappropriateStatutoryConsultee: optionalCheckbox,
    delayCauseContactingClinician: optionalCheckbox,
    delayCauseContactingStatutoryConsultee: optionalCheckbox,
    delayCauseStatutoryConsulteeMeeting: optionalCheckbox,
    delayCauseProviderArrangingInterpreter: optionalCheckbox,
    delayCauseRemoteAccess: optionalCheckbox,
    // The group's "at least one" rule must report against one of its fields;
    // the multi-select shows any option's error for the whole group.
    delayCauseOther: optionalCheckbox.test("at-least-one-delay-cause-selected", "Please select at least one cause of the delay", (_, ctx) => {
        const parent = ctx.parent;
        if (parent.delayedOutsideSoadControl !== true)
            return true;
        return assessmentDelayCauseOptions.some((o) => parent[o.field]);
    }),
    delayDetails: string()
        .default(null)
        .nullable()
        .when("delayCauseOther", {
        is: true,
        then: () => nonEmptyRequiredString.required("Please provide further details of the other cause"),
        otherwise: () => string().default(null).nullable(),
    }),
    delayDays: string()
        .default(null)
        .nullable()
        .when("delayedOutsideSoadControl", {
        is: true,
        then: (schema) => schema
            .required("Please enter approximately how many days the assessment was delayed")
            .matches(wholeNumber, {
            message: "Please enter the number of days as a whole number",
            excludeEmptyString: true,
        })
            // A blank or a non-number is left to the rules above, so each bad
            // value gets one error.
            .test("min-delay-days", `The number of days must be at least ${String(minAssessmentDelayDays)}`, (value) => value == null ||
            !wholeNumber.test(value) ||
            Number(value) >= minAssessmentDelayDays),
    }),
    completionStatus: soadAssessmentCompletionStatusSchema,
});
// diagnosis and history
export const soadReportFormPart2DataSchema = object({
    diagnosis: string()
        .default(null)
        .required("Please enter details on the patient’s diagnosis and history."),
    assessment: string()
        .default(null)
        .required("Please enter your assessment of the diagnosis."),
    completionStatus: soadAssessmentCompletionStatusSchema,
});
// treatment
export const soadReportFormPart3DataSchema = object({
    secondOpinionRequested: nonEmptyString
        .default("")
        .required("Please enter what the provider requested a second opinion for"),
    secondOpinionRequestedText: nonEmptyRequiredString,
    currentTreatment: string()
        .default(null)
        .required("Please enter details of the current treatment, including any medicine or ECT"),
    proposedTreatmentDifference: boolean()
        .default(null)
        .required("Please indicate whether the AC’s proposed treatment plans is different from the current treatment"),
    describeProposedTreatmentPlan: string()
        .nullable()
        .default("")
        .when("proposedTreatmentDifference", {
        is: true,
        then: () => string().required("Please enter details of the AC’s proposed treatment, including any medicine or ECT"),
        otherwise: () => string().default(null).nullable(),
    }),
    completionStatus: soadAssessmentCompletionStatusSchema,
});
// consultee details
export const soadReportFormPart4DataSchema = object({
    responsibleClinicianName: string()
        .default(null)
        .required("Please enter the name of the responsible clinician"),
    isResponsibleClinicianAC: boolean()
        .default(null)
        .required("Please confirm if you are the responsible clinician"),
    acName: nonEmptyString.when("isResponsibleClinicianAC", {
        is: false,
        then: () => nonEmptyRequiredString.required("Please enter the name of the Approved Clinician"),
        otherwise: () => string().default(null).nullable(),
    }),
    consultedWithAC: boolean()
        .default(null)
        .required("Please indicate whether you consulted with the Approved Clinician"),
    reasonForNotConsultingAC: nonEmptyString.when("consultedWithAC", {
        is: false,
        then: () => nonEmptyRequiredString.required("Please explain why you did not consult with the Approved Clinician"),
        otherwise: () => string().default(null).nullable(),
    }),
    firstConsulteeName: nonEmptyRequiredString.required("Please enter the name of the First Consultee"),
    firstConsulteeProfession: nonEmptyRequiredString.required("Please enter the profession of the First Consultee"),
    secondConsulteeName: nonEmptyRequiredString.required("Please enter the name of the Second Consultee"),
    secondConsulteeProfession: nonEmptyRequiredString.required("Please enter the profession of the Second Consultee"),
    additionalPerson: array()
        .of(object({
        nameOfAdditionalPerson: nonEmptyRequiredString.required("Please enter the name of the additional person"),
        relationshipToPatient: nonEmptyRequiredString.required("Please enter the relationship to the patient"),
    }))
        .default([]),
    completionStatus: soadAssessmentCompletionStatusSchema,
});
// authorisation
export const soadReportFormPart5DataSchema = object({
    secondOpinionCarriedOut: nonEmptyString
        .default("")
        .required("Please confirm the type of second opinion carried out"),
    treatmentPlanDecision: nonEmptyRequiredString.required("Please enter your decision on the treatment plan"),
    extentOfChanges: nonEmptyString.when("treatmentPlanDecision", {
        is: (decision) => decision === "modified",
        then: () => nonEmptyRequiredString.required("Please enter the extent of changes to the treatment plan"),
        otherwise: () => string().default(null).nullable(),
    }),
    changesMade: nonEmptyString.when("treatmentPlanDecision", {
        is: (decision) => decision === "modified",
        then: () => nonEmptyRequiredString.required("Please enter details of the changes made to the treatment plan"),
        otherwise: () => string().default(null).nullable(),
    }),
    notApprovedReason: nonEmptyString.when("treatmentPlanDecision", {
        is: (decision) => decision === "notApproved",
        then: () => nonEmptyRequiredString.required("Please enter the reason for not approving the treatment plan"),
        otherwise: () => string().default(null).nullable(),
    }),
    planAuthorisedEct: nonEmptyString.when(["secondOpinionCarriedOut", "treatmentPlanDecision"], {
        is: (secondOpinionCarriedOut, treatmentPlanDecision) => ["ect", "both"].includes(secondOpinionCarriedOut) &&
            ["current", "proposed", "modified"].includes(treatmentPlanDecision),
        then: () => nonEmptyRequiredString.required("Please enter details of the ECT authorised"),
        otherwise: () => string().default(null).nullable(),
    }),
    moreThan12Applications: boolean()
        .default(null)
        .when(["secondOpinionCarriedOut", "treatmentPlanDecision"], {
        is: (secondOpinionCarriedOut, treatmentPlanDecision) => ["ect", "both"].includes(secondOpinionCarriedOut) &&
            ["current", "proposed", "modified"].includes(treatmentPlanDecision),
        then: () => boolean().required("Please confirm that the treatment plan is within BNF limits"),
        otherwise: () => boolean().default(null).nullable(),
    }),
    planAuthorisedMedication: nonEmptyString.when(["secondOpinionCarriedOut", "treatmentPlanDecision"], {
        is: (secondOpinionCarriedOut, treatmentPlanDecision) => ["medication", "both"].includes(secondOpinionCarriedOut) &&
            ["current", "proposed", "modified"].includes(treatmentPlanDecision),
        then: () => nonEmptyRequiredString.required("Please enter details of the medication treatment plan"),
        otherwise: () => string().default(null).nullable(),
    }),
    bnfLimits: boolean()
        .default(null)
        .when(["secondOpinionCarriedOut", "treatmentPlanDecision"], {
        is: (secondOpinionCarriedOut, treatmentPlanDecision) => ["medication", "both"].includes(secondOpinionCarriedOut) &&
            ["current", "proposed", "modified"].includes(treatmentPlanDecision),
        then: () => boolean().required("Please confirm that the treatment plan is within BNF limits"),
        otherwise: () => boolean().default(null).nullable(),
    }),
    bnfLimitsFor: nonEmptyString.when("bnfLimits", {
        is: true,
        then: () => nonEmptyRequiredString.required("Please enter details of the BNF limits for the treatment plan"),
        otherwise: () => string().default(null).nullable(),
    }),
    offLabelMedication: boolean()
        .default(null)
        .when("planAuthorisedMedication", {
        is: (planAuthorisedMedication) => planAuthorisedMedication !== null,
        then: () => boolean()
            .default(null)
            .required("Please enter details of the off-label medication"),
        otherwise: () => boolean().default(null).nullable(),
    }),
    patientConsentStatus: nonEmptyRequiredString.required("Please enter the patient consent status"),
    notCapableConfirmationConsent: boolean()
        .default(null)
        .nullable()
        .when(["secondOpinionCarriedOut", "patientConsentStatus"], {
        is: (secondOpinionCarriedOut, patientConsentStatus) => (secondOpinionCarriedOut === "ect" ||
            secondOpinionCarriedOut === "both") &&
            patientConsentStatus === "notCapable",
        then: () => boolean().required("Please answer"),
        otherwise: () => boolean().default(null).nullable(),
    }),
    timeLimitCertificate: boolean()
        .default(null)
        .required("Please indicate whether a time limit certificate is required"),
    timeLimitCertificateDuration: string()
        .default(null)
        .nullable()
        .when("timeLimitCertificate", {
        is: true,
        then: () => nonEmptyRequiredString.required("Please outline the time limit"),
        otherwise: () => string().default(null).nullable(),
    }),
    timeLimitCertificateReason: string()
        .nullable()
        .default(null)
        .when("timeLimitCertificate", {
        is: true,
        then: () => nonEmptyRequiredString.required("Please outline the reason for the time limit"),
        otherwise: () => string().default(null).nullable(),
    }),
    interimS61Required: boolean()
        .default(null)
        .required("Please specify whether an interim s61 report is required"),
    interimS61DueDate: string()
        .nullable()
        .default("")
        .when("interimS61Required", {
        is: true,
        then: () => isoDateString.required("Please enter details of the AC’s proposed treatment, including any medicine or ECT"),
        otherwise: () => string().default(null).nullable(),
    }),
    interimS61Reasons: string()
        .nullable()
        .default("")
        .when("interimS61Required", {
        is: true,
        then: () => nonEmptyRequiredString.required("Please enter your reasons for requesting the s61 report"),
        otherwise: () => string().default(null).nullable(),
    }),
    notesForCqc: string().nullable().default(""),
    completionStatus: soadAssessmentCompletionStatusSchema,
});
// soad notes
export const soadReportFormPart6DataSchema = object({
    notes: string().nullable().default(null),
    completionStatus: string().oneOf(["completed"]).default("completed"),
});
export const exampleSRFData = [
    {
        // Part 1 - Assessment Details
        data: {
            dateOfAssessment: "2021-01-01",
            assessmentConducted: true,
            assessmentAddress: {
                address: "Assessment address",
                postalCode: "SE1",
                isConfirmed: true,
            },
            assessmentType: "Assessment type details",
            assessmentTypeText: "Assessment type text details",
            otherAssessments: [
                {
                    dateOfAssessment: "2021-01-01",
                    assessmentType: "Assessment type details",
                    assessmentTypeText: "Assessment type text details",
                    assessmentAddress: {
                        address: "Assessment address",
                        postalCode: "SE1",
                        isConfirmed: true,
                    },
                    reasonForAssessment: "Assessment details",
                    assessmentConducted: true,
                },
                {
                    dateOfAssessment: "2021-01-01",
                    assessmentType: "Assessment type details",
                    assessmentTypeText: "Assessment type text details",
                    assessmentAddress: {
                        address: "Assessment address",
                        postalCode: "SE1",
                        isConfirmed: true,
                    },
                    reasonForAssessment: "Assessment details",
                    assessmentConducted: true,
                },
            ],
            mentalHealthActSection: "Mental health act section details",
            mentalHealthActSectionText: "Mental health act section text details",
            ethnicity: "A",
            ethnicityText: "White - British",
            ethnicityDetermined: "other",
            ethnicityDeterminedOther: "Prefer not to say",
            delayedOutsideSoadControl: true,
            delayCauseProviderClinicalInformation: true,
            delayCauseFurtherInformationResponse: false,
            delayCausePatientRecordsAccess: false,
            delayCauseProviderArrangingAssessment: false,
            delayCauseProviderRescheduled: true,
            delayCauseInterpreterArrangements: false,
            delayCauseTravelOrLogistics: false,
            delayCauseSecurityOrEscorting: false,
            delayCausePatientTransferred: false,
            delayCausePatientAbsent: false,
            delayCausePatientTooUnwell: false,
            delayCauseInappropriateStatutoryConsultee: false,
            delayCauseContactingClinician: false,
            delayCauseContactingStatutoryConsultee: false,
            delayCauseStatutoryConsulteeMeeting: false,
            delayCauseProviderArrangingInterpreter: false,
            delayCauseRemoteAccess: false,
            delayCauseOther: true,
            delayDetails: "Ward staff were unavailable on the first arranged date",
            delayDays: "5",
            completionStatus: "completed",
        },
        context: {
            patient: { name: "John Doe", address: "123 address", postalCode: "SE4" },
            user: {
                name: "Dr Bob",
                email: "bob@bob.com",
                address: "123 hospital",
                postalCode: "SE5",
            },
        },
    },
    {
        // Part 2 - Diagnosis and history
        data: {
            diagnosis: "Diagnosis details",
            assessment: "Assessment details",
            completionStatus: "completed",
        },
    },
    {
        // Part 3 - Treatment
        data: {
            secondOpinionRequested: "both",
            secondOpinionRequestedText: "Both",
            currentTreatment: "Treatment details",
            proposedTreatmentDifference: true,
            describeProposedTreatmentPlan: "Proposed treatment details",
            completionStatus: "completed",
        },
    },
    {
        // Part 4 - Consultee details
        data: {
            responsibleClinicianName: "Consultee name",
            isResponsibleClinicianAC: true,
            acName: null,
            consultedWithAC: true,
            reasonForNotConsultingAC: null,
            firstConsulteeName: "First consultee name",
            firstConsulteeProfession: "First consultee profession",
            secondConsulteeName: "Second consultee name",
            secondConsulteeProfession: "Second consultee profession",
            additionalPerson: [
                {
                    nameOfAdditionalPerson: "Additional person name",
                    relationshipToPatient: "Relationship to patient",
                },
                {
                    nameOfAdditionalPerson: "Additional person name",
                    relationshipToPatient: "Relationship to patient",
                },
            ],
            completionStatus: "completed",
        },
    },
    {
        // Part 5 - Authorisation
        data: {
            secondOpinionCarriedOut: "both",
            treatmentPlanDecision: "current",
            extentOfChanges: null,
            changesMade: null,
            notApprovedReason: null,
            planAuthorisedEct: "ECT Plan details",
            moreThan12Applications: true,
            planAuthorisedMedication: "Medication Plan details",
            bnfLimits: true,
            bnfLimitsFor: "singleDrug",
            offLabelMedication: false,
            patientConsentStatus: "capable",
            notCapableConfirmationConsent: null,
            timeLimitCertificate: true,
            timeLimitCertificateDuration: "A couple of weeks",
            timeLimitCertificateReason: "Reason for time limit",
            interimS61Required: true,
            interimS61DueDate: "2021-01-01",
            interimS61Reasons: "Interim S61 reason details",
            notesForCqc: "Notes for the CQC",
            completionStatus: "completed",
        },
    },
    // Part 6 - SOAD notes for CQC
    {
        data: {
            notes: "Notes",
            completionStatus: "completed",
        },
    },
];
//# sourceMappingURL=srf.js.map